import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { useOrganization } from '../hooks/useOrganization';

const OwnerTransactions = () => {
  const { organization } = useOrganization();
  const [transactions, setTransactions] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [equityAccounts, setEquityAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  

  useEffect(() => {
    fetchTransactions();
    fetchAccounts();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/api/owner-transactions');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error('Failed to fetch owner transactions');
    }
  };

  const fetchAccounts = async () => {
    try {
      const [cashResponse, equityResponse] = await Promise.all([
        axios.get('/api/accounts/cash'),
        axios.get('/api/accounts/chart-of-accounts?type=EQUITY')
      ]);
      setCashAccounts(cashResponse.data || []);
      setEquityAccounts(equityResponse.data || []);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    }
  };

  const handleCreateTransaction = async (formData) => {
    try {
      await axios.post('/api/owner-transactions', {
        transactionType: formData.transactionType,
        amount: formData.amount,
        description: formData.description,
        transactionDate: formData.transactionDate,
        cashAccountId: formData.cashAccountId,
        equityAccountId: formData.equityAccountId,
        notes: formData.notes
      });
      toast.success('Owner transaction created successfully');
      setShowForm(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create transaction');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await axios.delete(`/api/owner-transactions/${transactionId}`);
        toast.success('Transaction deleted successfully');
        fetchTransactions();
      } catch (error) {
        toast.error('Failed to delete transaction');
      }
    }
  };

  const TransactionForm = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      transactionType: 'INVESTMENT',
      amount: '',
      description: '',
      transactionDate: new Date().toISOString().split('T')[0],
      cashAccountId: '',
      equityAccountId: '',
      notes: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Add Owner Transaction</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Transaction Type</label>
            <select
              value={formData.transactionType}
              onChange={(e) => setFormData({...formData, transactionType: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="INVESTMENT">Investment (Owner puts money in)</option>
              <option value="WITHDRAWAL">Withdrawal (Owner takes money out)</option>
              <option value="CONTRIBUTION">Contribution (Owner adds assets)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g., Owner capital investment"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              value={formData.transactionDate}
              onChange={(e) => setFormData({...formData, transactionDate: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cash Account</label>
            <select
              value={formData.cashAccountId}
              onChange={(e) => setFormData({...formData, cashAccountId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Cash Account</option>
              {cashAccounts.map(account => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Equity Account</label>
            <select
              value={formData.equityAccountId}
              onChange={(e) => setFormData({...formData, equityAccountId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Equity Account</option>
              {equityAccounts.map(account => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              rows="3"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Create Transaction
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'INVESTMENT':
      case 'CONTRIBUTION':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'WITHDRAWAL':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'INVESTMENT':
      case 'CONTRIBUTION':
        return 'bg-green-100 text-green-800';
      case 'WITHDRAWAL':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Owner Transactions</h1>
      </div>

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Owner Transaction
        </button>
      )}

      {showForm ? (
        <TransactionForm onSubmit={handleCreateTransaction} onCancel={() => setShowForm(false)} />
      ) : (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cash Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Equity Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map(transaction => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(transaction.transaction_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        {getTransactionIcon(transaction.transaction_type)}
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getTransactionColor(transaction.transaction_type)}`}>
                          {transaction.transaction_type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(transaction.amount, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.cash_account_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.equity_account_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerTransactions;